import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { dateLabelWithMaybeYear, dbDate } from "@caredna-marlin/utils/dates";
import { retryFetch } from "@caredna-marlin/utils/client";
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  IconButton,
  Stack,
  Typography,
  Modal,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  TextField,
  InputAdornment,
} from "@mui/material";

import {
  isSameDay,
} from "date-fns";
import {
  dayjsTimezone,
  Eventcalendar,
  MbscEventcalendarView,
  MbscEventClickEvent,
  MbscResource,
} from "@mobiscroll/react";

import "@mobiscroll/react/dist/css/mobiscroll.min.css";

import {
  ActivePatientOrderBy,
  CommandIndexProvidersQuery,
  DriveTimeOptimizerQuery,
  OrderDirection,
  ShiftStatusEnum,
  useDriveTimeOptimizerLazyQuery,
  useUpdateCalendarBlockMutationMutation
} from "~/graphql/__generated__/graphql";
import { VisitStatusKey } from "~/components/command/VisitStatusKey";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import MapIcon from "@mui/icons-material/Map";
import SearchIcon from "@mui/icons-material/Search";
import DatePickerPopup from "~/components/command/DatePickerPopup";
import { CommandCenterLocationState, useCalendarDateTimeConverter, useDateSelector } from "~/components/command/DateSelector";
import RouteIndexView from "../route";
import { RouteLocalCalendarBlock, RouteLocalVisitResource, RouteProviderContext, RouteShiftContext } from "../route/types";
import CloseIcon from "@mui/icons-material/Close";
import { CalendarPopup } from "~/components/common/calendar/CalendarPopup";
import CommandCenterStats from "./CommandCenterStats";
import ShiftPopup from "~/components/command/ShiftPopup";
import CalendarBlockPopup from "~/components/command/CalendarBlockPopup";
import VisitPopup from "~/components/command/VisitPopup";
import CancelVisitDialog from "~/components/command/CancelVisitDialog";
import { CalendarError, CalendarErrorType, CustomMbscCalendarEventData, LocalVisitRequest } from "~/utils/eventsHelper";
import { buildDriveOptimizerRequestData, OptimizerInitialState, type LocalVisitResourceFriction } from "~/utils/driveTimeOptimizerConversion";
import { applyAssignMissingProvidersFriction, applyProviderOptimizeFriction } from "~/utils/driveTimeOptimizerFriction";
import FilterMenu, { useDefaultFilters } from "~/components/command/FilterMenu";
import PermissionOrHide from "~/components/common/PermissionOrHide";
import { useHasPermission, Permission, useOrganizationalUnitSegmentationEnabled } from "~/hooks/permissions";
import CreateOverrideVisit from "~/components/scheduler/CreateOverrideVisit";
import ProviderCalendarItem from "~/components/command/ProviderCalendarItem";
import PatientCalendarItem from "~/components/command/PatientCalendarItem";
import { useSnackbar } from "~/hooks/useSnackbar";
import CalendarHeaderContent from "~/components/command/CalendarHeaderContent";
import StyledCalendar from "~/components/command/StyledCalendar";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { getTimeFormat } from "@caredna-marlin/utils/dates";
import { DEFAULT_SHUFFLER_LEAD_TIME_MINUTES } from "@caredna-marlin/utils/organizationTypes";
import { useZonedDateFormat } from "~/providers/ZonedDateFormatProvider";
import useVisitDragDrop from '~/hooks/useVisitDragDrop';
import { useUnsavedChangesWarning } from '~/hooks/useUnsavedChangesWarning';
import { LocalVisit, LocalVisitResourcePatch } from '~/hooks/visitDragDropReducer';
import { COMMAND_INDEX_PROVIDERS_QUERY } from '~/queries/forms';
import { roundTime15 } from "@caredna-marlin/utils/dates";
import { useMobiscrollProviderCalendarEvents, useConnections } from '~/hooks/useMobiscrollProviderCalendar';
import useMobiscrollPatientCalendarEvents from './mobiscrollPatientCalendarEvents';
import renderCalendarEvent from './CalendarEvent';
import type { CalendarEventRenderLookups } from './CalendarEvent';
import AddToCalendarMenu from '~/components/command/AddToCalendarMenu';
import OptimizeButton, { type AdvancedOptimizeSettings, type OptimizeButtonStatus } from '~/components/command/OptimizeButton';
import { useCurrentOrg } from '~/providers/GraphqlProvider';
import useDebouncedState from '~/hooks/useDebouncedState';
import { afterBuffer, beforeBuffer, eventsHaveChanged, resourcesHaveChanged } from '~/utils/mobiscrollEventFormatter';
import MultiResourceProviderAssign from '~/components/command/MultiResourceProviderAssign';
import { assignVisitToState, assignProviderToResource, getAssignedProviders, getLocalVisitRequest } from '~/hooks/visitDragDropUtils';
import VisitRequestPopup from '~/components/command/VisitRequestPopup';
import { OverrideModalState } from '~/components/scheduler/overrideVisitTypes';
import { UNASSIGNED_PROVIDER_ID, UNASSIGNED_PROVIDER_TYPE_ID } from '~/utils/visitsHelper';
import { buildRouteLocalData } from './routeLocalDataMapper';
import { getOptimizationIssueStats, shouldResetOptimizationResult, shouldWaitForOptimizationDriveTimes } from '~/components/command/optimizationReviewUtils';
import useUtilizationPerShift from '~/components/command/hooks/useUtilizationPerShift';
import { groupBy } from 'lodash';

const style = {
  position: "absolute",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80vw",
  height: "80vh",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};
const ACTIVE_PATIENTS_LIMIT = 100;

type CommandActivePatientsQueryResult = {
  activePatients: {
    id: number;
    firstName?: string | null;
    lastName?: string | null;
  }[];
};

type CommandActivePatientsQueryVariables = {
  orderBy: ActivePatientOrderBy;
  offset: number;
  limit: number;
};

// First, create helper type for the popover event
type PopoverEvent = {
  type: "shift" | "visit" | "calendar_block";
  id: number;
};

dayjs.extend(utc);
dayjs.extend(timezone);
dayjsTimezone.dayjs = dayjs;

const matchesName = (query: string, firstName?: string | null, lastName?: string | null) => {
  const first = firstName?.toLowerCase() || "";
  const last = lastName?.toLowerCase() || "";
  const full = `${first} ${last}`.trim();
  return first.includes(query) || last.includes(query) || full.includes(query);
};

const CALENDAR_VIEW_CONFIG: MbscEventcalendarView = {
  timeline: { type: "day" },
};

export default function CommandIndexView() {
  // COMPONENT STATE
  const { timeFormatPreference } = useZonedDateFormat();
  const locationState = useLocation().state as CommandCenterLocationState;
  const apolloClient = useApolloClient();
  const { date, setDate, previousDay, nextDay, today } = useDateSelector();
  const defaultFilters = useDefaultFilters();
  const [filters, setFilters] = useState(defaultFilters);
  const [viewMode, setViewMode] = useState<"provider" | "patient">(() => {
    const stored = localStorage["commandCenterViewMode"];
    return stored === "patient" ? "patient" : "provider";
  });

  const [showOriginalPositions, setShowOriginalPositions] = useState(false);
  const [patientSearchQuery, debouncedPatientSearchQuery, setPatientSearchQuery] = useDebouncedState("", 30);
  const [providerSearchQuery, debouncedProviderSearchQuery, setProviderSearchQuery] = useDebouncedState("", 30);
  // Ref to store timeout ID for icon change
  const driveTimeOptimizerIconTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const optimizerLogInitialStateRef = useRef<OptimizerInitialState | null>(null);
  const [pendingOptimization, setPendingOptimization] = useState<{
    phase: 'query' | 'review' | 'no-optimizations';
    requiresDriveTimeUpdate: boolean;
  } | null>(null);
  const [appliedIgnoreKeepResults, setAppliedIgnoreKeepResults] = useState(false);

  // State to track the button icon
  const [driveTimeOptimizerIcon, setDriveTimeOptimizerIcon] = useState<OptimizeButtonStatus>('default');
  const [optimizerLogId, setOptimizerLogId] = useState<number | null>(null);
  // Consolidated override modal state - replaces separate selectedProviderIdForVisit and visitIdToBeEdited
  const [overrideModalState, setOverrideModalState] = useState<OverrideModalState | null>(null);

  const [errorsAndWarnings, setErrorsAndWarnings] = useState<Map<string, (CalendarError | null)[]>>(new Map());

  const hasCreateDragEventsPermission = useHasPermission(Permission.createDragEvents) ?? false;
  const organizationalUnitSegmentationEnabled = useOrganizationalUnitSegmentationEnabled();

  const orgConfig = useOrgConfig();

  const currentOrg = useCurrentOrg();

  // Filters are stored per org; reload them on org switch (this view doesn't remount). Only do
  // this on an actual switch (a previously-known org id changing to a different one) -- not on
  // initial mount, when `orgConfig.loading` also flips from true to false. Resetting on mount too
  // would fire an extra `setFilters` render on every page load, right as Mobiscroll is doing its
  // own internal drag setup, which made drag registration considerably flakier under CI load.
  const defaultFiltersRef = useRef(defaultFilters);
  defaultFiltersRef.current = defaultFilters;
  const loadedOrgIdRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (orgConfig.loading || currentOrg?.id === undefined) {
      return;
    }
    if (loadedOrgIdRef.current !== undefined && loadedOrgIdRef.current !== currentOrg.id) {
      setFilters(defaultFiltersRef.current);
    }
    loadedOrgIdRef.current = currentOrg.id;
  }, [currentOrg?.id, orgConfig.loading]);

  const shufflerLeadTime = currentOrg?.settings?.shufflerLeadTime ?? DEFAULT_SHUFFLER_LEAD_TIME_MINUTES;

  const calendarRef = useRef<Eventcalendar>(null);

  const hasProviderLocationPermission = useHasPermission(Permission.viewProviderLocations);

  const navigate = useNavigate();

  const [routeModalOpen, setRouteModalOpen] = useState(false);
  const [routeProviderId, setRouteProviderId] = useState(-1);
  const [routeProviderName, setRouteProviderName] = useState("");
  const [routeLocalDataSnapshot, setRouteLocalDataSnapshot] = useState<{
    localVisitResources: RouteLocalVisitResource[];
    localCalendarBlocks: RouteLocalCalendarBlock[];
    providerContext: RouteProviderContext;
    shiftContext?: RouteShiftContext;
  } | null>(null);

  const [visitIdToBeCanceled, setVisitIdToBeCanceled] = useState<number | null>(null);
  const popoverEventElement = useRef<null | HTMLElement>(window.document.body);
  const [popoverEvent, setPopoverEvent] = useState<null | PopoverEvent>(null);
  
  const snackbar = useSnackbar();
  const [lastAddedVisitId, setLastAddedVisitId] = useState<number | null>(null);

  const activePatientOrderBy = useMemo<ActivePatientOrderBy>(() => ({
    addressCity: null,
    addressState: null,
    addressZip: null,
    dateOfBirth: null,
    firstName: { direction: OrderDirection.Asc, priority: 2 },
    id: null,
    lastName: { direction: OrderDirection.Asc, priority: 1 },
    nextVisitStartTime: null,
    phone: null,
  }), []);

  // GRAPHQL QUERIES
  const [runDataOptimizerQuery, {loading: driveTimeOptimizerLoading }] = useDriveTimeOptimizerLazyQuery({
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      const requiresDriveTimeUpdate = handleDriveTimeOptimizerData(data);
      setPendingOptimization({
        phase: 'review',
        requiresDriveTimeUpdate,
      });
    },
    onError: (err) => {
      console.error('Drive time optimizer query error: ', err);
      setDriveTimeOptimizerIcon('error');
      setPendingOptimization(null);
      driveTimeOptimizerIconTimeoutRef.current = setTimeout(() => {
        setDriveTimeOptimizerIcon('default');
      }, 3000);
    },
  });

  const { data: providerData, loading: providerLoading, error: providerError } = useQuery<CommandIndexProvidersQuery>(
    COMMAND_INDEX_PROVIDERS_QUERY,
    {
      variables: { organizationalUnitSegmentationEnabled: organizationalUnitSegmentationEnabled === true },
      // NOTE: Do NOT filter by only active providers because we need to show inactive providers in the command center.
      onError(error) {
        console.error(error);
      },  
    }
  );

  const activeProvidersList = providerData?.commandIndexProviders?.providers?.filter((p) => p.active);
  const selectedProviderForOverrideVisit = overrideModalState?.mode === 'provider-start' 
    ? overrideModalState.provider 
    : undefined;

  const { data: activePatientsData } = useQuery<CommandActivePatientsQueryResult, CommandActivePatientsQueryVariables>(
    COMMAND_ACTIVE_PATIENTS_QUERY,
    {
      variables: {
        orderBy: activePatientOrderBy,
        offset: 0,
        limit: ACTIVE_PATIENTS_LIMIT + 1,
      },
    }
  );

  // MUTATIONS
  const [scheduleOverrideVisit, { loading: isSavingVisitRequest }] = useMutation(SCHEDULE_VISIT_FROM_VISIT_REQUEST);
  const [updateVisitResourceMutation, { loading: isSavingVisitResource }] = useMutation(UPDATE_VISIT_RESOURCE);
  const [updateCalendarBlockMutation, { loading: isSavingCalendarBlock }] = useUpdateCalendarBlockMutationMutation();
  const [createOptimizerLogInitialStateUploadUrl] = useMutation(CREATE_OPTIMIZER_LOG_INITIAL_STATE_UPLOAD_URL);
  
  // When saving a bevy of visit resources, track progress - and pause command center polling.
  const [updateProgress, setUpdateProgress] = useState<null | [number, number]>(null)

  // MOBISCROLL CALENDAR STATE MANAGEMENT
  // State management for visit calendar events and drag and drop events
  // 15s polling by default, disable it while we're in the process of saving everything.'
  const dragDropState = useVisitDragDrop(date, updateProgress === null ? 15_000 : 3_600_000);
  const {
    dispatch,
    localVisitResources,
    localShifts,
    localCalendarBlocks,
    localVisits,
    localVisitRequests,
    dbChangeDetected,
    loading: combinedLoading,
    driveTimesLoading,
    modifiedResourceIds,
    modifiedCalendarBlocks,
    visitMetaDataLookup,
    originalVisitResources,
    driveTimeOptimizations,
    skippedVisitResources,
    pendingDriveTimeShiftIds,
    hasPendingDriveTimeUpdates,
    careRequirementVisits,
    careRequirementVisitsLoading,
  } = dragDropState;

  const anyLoading = providerLoading || combinedLoading;
  const isPatientView = viewMode === "patient";
  const dragHandlersEnabled = !isPatientView && hasCreateDragEventsPermission;

  const fetchedActivePatients = activePatientsData?.activePatients ?? [];
  const activePatients = fetchedActivePatients.slice(0, ACTIVE_PATIENTS_LIMIT);
  const hasMorePatients = fetchedActivePatients.length > ACTIVE_PATIENTS_LIMIT;

  const matchingPatientIds = useMemo(() => {
    if (!debouncedPatientSearchQuery.trim()) {
      return new Set<number>();
    }

    const query = debouncedPatientSearchQuery.toLowerCase().trim();
    const matchedIds = new Set<number>();

    activePatients.forEach(patient => {
      if (matchesName(query, patient.firstName, patient.lastName)) {
        matchedIds.add(patient.id);
      }
    });

    localVisitResources.forEach(vr => {
      const patient = vr.visit.patient;
      if (patient?.id && matchesName(query, patient.firstName, patient.lastName)) {
        matchedIds.add(patient.id);
      }
    });

    return matchedIds;
  }, [activePatients, debouncedPatientSearchQuery, localVisitResources]);

  const matchingProviderData = useMemo(() => {
    if (!debouncedProviderSearchQuery.trim()) {
      return { providerIds: new Set<number>(), providerTypeIds: new Set<number>() };
    }

    const query = debouncedProviderSearchQuery.toLowerCase().trim();
    const matchingProviders = (providerData?.commandIndexProviders?.providers ?? [])
      .filter((provider: any) => matchesName(query, provider.firstName, provider.lastName));
    
    const providerIds = new Set(matchingProviders.map((provider: any) => provider.id));
    const providerTypeIds = new Set(matchingProviders.map((provider: any) => provider.providerTypeId));

    // Never filter out unassigned visits
    providerIds.add(UNASSIGNED_PROVIDER_ID);
    providerTypeIds.add(UNASSIGNED_PROVIDER_TYPE_ID)
    
    return { providerIds, providerTypeIds };
  }, [debouncedProviderSearchQuery, providerData?.commandIndexProviders?.providers]);

  const providerLookup = useMemo(
    () =>
      Object.fromEntries(
        (providerData?.commandIndexProviders?.providers ?? []).map((provider) => [provider.id, provider])
      ) as Record<number, CommandIndexProvidersQuery["commandIndexProviders"]["providers"][number]>,
    [providerData?.commandIndexProviders?.providers]
  );

  const utilizationPerShift = useUtilizationPerShift(localVisitResources, localShifts, localCalendarBlocks);
  const calendarEventRenderLookups = useMemo<CalendarEventRenderLookups>(() => ({
    visitResourceById: new Map(localVisitResources.map((visitResource) => [visitResource.id, visitResource])),
    visitRequestById: new Map(localVisitRequests.map((visitRequest) => [visitRequest.id, visitRequest])),
    shiftById: new Map(localShifts.map((shift) => [shift.id, shift])),
    calendarBlockById: new Map(localCalendarBlocks.map((calendarBlock) => [calendarBlock.id, calendarBlock])),
  }), [localCalendarBlocks, localShifts, localVisitRequests, localVisitResources]);

  const pendingNewVisitsToBeSaved = localVisits.filter((visit) => visitMetaDataLookup[visit.id]);

  const existentVisitResourcesToBeUpdated = localVisitResources
    .filter((vr) => modifiedResourceIds.has(vr.id))
    .filter((vr) => !pendingNewVisitsToBeSaved.find((pnv) => pnv.id === vr.visitId));

  const optimizationIssueStats = useMemo(() => {
    return getOptimizationIssueStats({
      date,
      originalVisitResources,
      localVisitResources,
      localVisits,
      localVisitRequests,
      providers: providerData?.commandIndexProviders?.providers ?? [],
      localShifts,
      localCalendarBlocks,
      careRequirementVisits,
      skippedVisitResources,
      orgConfig,
    });
  }, [
    careRequirementVisits,
    localCalendarBlocks,
    localShifts,
    localVisitRequests,
    localVisitResources,
    localVisits,
    orgConfig,
    originalVisitResources,
    providerData?.commandIndexProviders?.providers,
    skippedVisitResources,
  ]);

  const isOptimizationTimelineBlocked = pendingOptimization !== null;
  const isOptimizationReviewLoading = isOptimizationTimelineBlocked || driveTimesLoading || pendingDriveTimeShiftIds.size > 0;

  // Mobiscroll provider calendar event formatting
  const {
    visitEvents: providerVisitEvents,
    shiftEvents,
    calendarBlockEvents,
    calendarResources: providerCalendarResources,
    originalVisitEvents,
    calendarColors,
    onEventCreate,
    onEventUpdate,
    onEventDragStart,
    onEventDragEnd,
    onResetDragDrop,
    continuityOfCareShiftIds,
    toggleCollapsedGroup,
    medarriveEvent,
    setMedarriveEvent,
    shouldOpenMultiResourceAssignPopup,
    setShouldOpenMultiResourceAssignPopup
  } = useMobiscrollProviderCalendarEvents(
    anyLoading,
    dragDropState,
    providerData?.commandIndexProviders ?? { providers: [], providersByType: [] },
    providerError || providerLoading ? undefined : providerData?.organizationalUnitOrgUser,
    providerError || providerLoading ? undefined : providerData?.organizationalUnit?.some(unit => unit.active),
    lastAddedVisitId,
    filters,
    date,
    showOriginalPositions
  );

  const onReset = useCallback(() => {
    setErrorsAndWarnings(new Map());
    onResetDragDrop();
  }, [onResetDragDrop]);

  // Unsaved changes warning hook
  useUnsavedChangesWarning(modifiedResourceIds.size > 0 || Object.keys(modifiedCalendarBlocks).length > 0, onReset);

  // Mobiscroll patient calendar event formatting
  const {
    visitEvents: patientVisitEvents,
    calendarResources: patientCalendarResources,
  } = useMobiscrollPatientCalendarEvents({
    loading: anyLoading,
    visitResources: localVisitResources,
    lastAddedVisitId,
    filters,
    currentProviderId: currentOrg?.providerId ?? null,
    providers: providerData?.commandIndexProviders?.providers ?? [],
    localShifts,
    localVisits,
    localVisitRequests,
    localCalendarBlocks,
    orgConfig,
    activePatients,
    careRequirementVisits: dragDropState.careRequirementVisits,
  });

  const connections = useConnections(localVisits, originalVisitResources);
  const calendarDateTime = useCalendarDateTimeConverter(date, isPatientView ? patientVisitEvents : shiftEvents);

  const filteredPatientCalendarResources = useMemo(() => {
    if (!isPatientView || !debouncedPatientSearchQuery.trim()) {
      return patientCalendarResources;
    }

    return patientCalendarResources.filter(resource => {
      const resourceId = typeof resource.id === "number" ? resource.id : parseInt(`${resource.id}`, 10);
      return matchingPatientIds.has(resourceId);
    });
  }, [debouncedPatientSearchQuery, isPatientView, matchingPatientIds, patientCalendarResources]);

  const filteredProviderCalendarResources = useMemo(() => {
    if (isPatientView || !debouncedProviderSearchQuery.trim()) {
      return providerCalendarResources;
    }

    const filtered = providerCalendarResources
      .filter(resource => {
        const resourceId = resource.id;

        if (typeof resourceId === 'string' && resourceId.startsWith('type_')) {
          const typeId = parseInt(resourceId.replace('type_', ''), 10);
          return matchingProviderData.providerTypeIds.has(typeId);
        }

        const numericId = typeof resourceId === "number" ? resourceId : parseInt(`${resourceId}`, 10);
        return matchingProviderData.providerIds.has(numericId);
      })
      .map(resource => {
        if (typeof resource.id === 'string' && resource.id.startsWith('type_') && resource.children) {
          return {
            ...resource,
            children: resource.children.filter((child: any) => 
              matchingProviderData.providerIds.has(child.id)
            )
          };
        }

        return resource;
      });

    return filtered;
  }, [debouncedProviderSearchQuery, isPatientView, matchingProviderData.providerIds, matchingProviderData.providerTypeIds, providerCalendarResources]);

  const calendarResources = isPatientView ? filteredPatientCalendarResources : filteredProviderCalendarResources;

  const calendarData = useMemo(() => {
    if (isPatientView) {
      return [...patientVisitEvents];
    }

    const ghostEvents: CustomMbscCalendarEventData[] = originalVisitEvents?.length ? [...originalVisitEvents] as CustomMbscCalendarEventData[] : [];

    if (debouncedProviderSearchQuery.trim()) {
      const filteredShiftEvents = shiftEvents.filter((event: any) => {
        return event.resource && matchingProviderData.providerIds.has(event.resource);
      });
      
      const filteredProviderVisitEvents = providerVisitEvents.filter((event: any) => {
        return event.resource && matchingProviderData.providerIds.has(event.resource);
      });
      
      return [...filteredShiftEvents, ...filteredProviderVisitEvents, ...calendarBlockEvents, ...ghostEvents];
    }

    return [...ghostEvents, ...shiftEvents, ...providerVisitEvents, ...calendarBlockEvents];
  }, [calendarBlockEvents, debouncedProviderSearchQuery, isPatientView, matchingProviderData.providerIds, originalVisitEvents, patientVisitEvents, providerVisitEvents, shiftEvents]);

  const eventsCache = useRef<CustomMbscCalendarEventData[]>([]);
  const resourcesCache = useRef<MbscResource[]>([]);

  // Extra guard for mobiscroll data events array so it doesn't trigger re-renders
  // @TODO: Use useStableArray instead but right now it's causing issues when adding new events and drive times.
  const stableCalendarData = useMemo(() => {
    const hasChanges = eventsHaveChanged(eventsCache.current, calendarData);

    if (!hasChanges) {
      return eventsCache.current;
    }
    
    eventsCache.current = calendarData;
    return calendarData;
  }, [calendarData]);

  const stableResources = useMemo(() => {
    const hasChanges = resourcesHaveChanged(resourcesCache.current, calendarResources);
    if (!hasChanges) {
      return resourcesCache.current;
    }
    resourcesCache.current = calendarResources;
    return calendarResources;
  }, [calendarResources]);

  const colors = useMemo(()=> isPatientView ? undefined : calendarColors, [calendarColors, isPatientView]);

  // CALLBACKS
  const onMultiResourceAssign = async (_visit: LocalVisitRequest, assignedResourceProviderMap: Record<number, number>) => {
    if (!medarriveEvent) return;
    
    let updatedData = medarriveEvent;
    Object.entries(assignedResourceProviderMap).forEach(([visitResourceId, providerId]) => {
      updatedData = assignProviderToResource(updatedData, Number(visitResourceId), providerId);
    });
    
    // Defer dispatch to allow Mobiscroll to complete any internal processing
    // This will prevent maximum update depth exceeded error on the future if anything else causes weird cascade cycles.
    requestAnimationFrame(() => {
      assignVisitToState(updatedData, dragDropState.dispatch);
    });
  };

  const handleViewModeChange = useCallback((_event: MouseEvent<HTMLElement>, nextMode: "provider" | "patient" | null) => {
    if (nextMode) {
      setPopoverEvent(null);
      setVisitIdToBeCanceled(null);
      setOverrideModalState(null);

      setViewMode(nextMode);
      localStorage["commandCenterViewMode"] = nextMode;
    }
  }, []);

  // Calendar blocks now use the same styling pattern as visit resources
  // No custom CSS injection needed
  const updateErrorForVisit = useCallback((
    localVisit: LocalVisit,
    errorMessage: string | null,
    type: CalendarErrorType
  ) => {
    const key = `${localVisit.id}`;
    const currentErrors = errorsAndWarnings.get(key) ?? [];

    const filteredErrors = currentErrors.filter((e) => e?.type !== type);

    if (errorMessage) {
      filteredErrors.push({
        type,
        visit: localVisit,
        visitType: orgConfig['visitTypes'][localVisit.visitTypeId],
        message: errorMessage,
      });
    }

    const newMap = new Map(errorsAndWarnings);
    newMap.set(key, filteredErrors);

    setErrorsAndWarnings(newMap);
  }, [errorsAndWarnings, orgConfig]);

  const handleRouteModalOpen = useCallback((providerId: string, name: string) => {
    if (!providerId) {
      return;
    }

    const parsedProviderId = parseInt(providerId, 10);
    setRouteLocalDataSnapshot(buildRouteLocalData({
      providerId: parsedProviderId,
      date,
      localVisitResources,
      localCalendarBlocks,
      localShifts,
      providerAddressCoordinates: providerLookup[parsedProviderId]?.addressCoordinates ?? null,
      orgConfig,
    }));
    setRouteProviderId(parsedProviderId);
    setRouteProviderName(name);
    setRouteModalOpen(true);
  }, [date, localCalendarBlocks, localShifts, localVisitResources, orgConfig, providerLookup]);

  const hasUnsavedRouteChanges = useCallback((providerId: number): boolean => {
    const selectedDbDate = dbDate(date);
    const isOnSelectedDay = (value: string | Date) => dbDate(value) === selectedDbDate;

    const dbVisitResourceById = new Map(dragDropState.dbVisitResources.map((vr) => [vr.id, vr]));
    const dbCalendarBlockById = new Map(dragDropState.dbCalendarBlocks.map((cb) => [cb.id, cb]));

    for (const modifiedResourceId of modifiedResourceIds) {
      const localResource = localVisitResources.find((vr) => vr.id === modifiedResourceId);
      if (!localResource) {
        continue;
      }

      if (localResource.providerId === providerId && isOnSelectedDay(localResource.startTime)) {
        return true;
      }

      const dbResource = dbVisitResourceById.get(modifiedResourceId);
      if (!dbResource) {
        continue;
      }
      if (dbResource.providerId === providerId && isOnSelectedDay(dbResource.startTime)) {
        return true;
      }
    }

    for (const [calendarBlockId, patch] of Object.entries(modifiedCalendarBlocks)) {
      const numericId = Number(calendarBlockId);
      const dbBlock = dbCalendarBlockById.get(numericId);
      if (!dbBlock) {
        continue;
      }

      const localProviderId = patch.providerId ?? dbBlock.providerId;
      const localStart = patch.startTime ?? dbBlock.startTime;
      if (localProviderId === providerId && isOnSelectedDay(localStart)) {
        return true;
      }

      if (dbBlock.providerId === providerId && isOnSelectedDay(dbBlock.startTime)) {
        return true;
      }
    }

    return false;
  }, [
    date,
    dragDropState.dbCalendarBlocks,
    dragDropState.dbVisitResources,
    localCalendarBlocks,
    localVisitResources,
    modifiedCalendarBlocks,
    modifiedResourceIds,
  ]);

  const optimizeThresholdMs = Date.now() + shufflerLeadTime * 60_000;
  const hasOptimizableResources = localVisitResources.some((resource) => {
    if (!resource.startTime) return false;

    const driveTimeMinutes = resource.interVisitDriveTime || resource.startOfShiftDriveTime || 0;
    const visitDepartureTimeMs = new Date(resource.startTime).getTime() - driveTimeMinutes * 60_000;

    return visitDepartureTimeMs > optimizeThresholdMs;
  });

  const isOptimizeDisabled =
    driveTimeOptimizerLoading ||
    combinedLoading ||
    !hasOptimizableResources ||
    careRequirementVisitsLoading ||
    isOptimizationTimelineBlocked;

  const runDriveTimeOptimization = useCallback((
    arrivalWindowAdjustment: number | undefined,
    providerId?: number,
    assignMissingProviders?: boolean,
    advanced?: Pick<AdvancedOptimizeSettings, "defaultStartTimeFriction" | "defaultAssignmentFriction">,
    ignoreShouldKeepOptimizationResult = false,
  ) => {
    if (driveTimeOptimizerIconTimeoutRef.current) {
      clearTimeout(driveTimeOptimizerIconTimeoutRef.current);
      driveTimeOptimizerIconTimeoutRef.current = null;
    }
    setAppliedIgnoreKeepResults(ignoreShouldKeepOptimizationResult);
    setPendingOptimization({
      phase: 'query',
      requiresDriveTimeUpdate: false,
    });
    setDriveTimeOptimizerIcon("loading");

    let visitResources = localVisitResources;
    if (!advanced && assignMissingProviders) {
      visitResources = applyAssignMissingProvidersFriction(localVisitResources, localShifts);
    } else if (!advanced && providerId != null) {
      visitResources = applyProviderOptimizeFriction(localVisitResources, providerId);
    }
    const freezeDefaults = !advanced && (providerId != null || !!assignMissingProviders);
    const defaultStartTimeFriction: LocalVisitResourceFriction | undefined = advanced
      ? advanced.defaultStartTimeFriction
      : freezeDefaults ? "frozen" : undefined;
    const defaultAssignmentFriction: LocalVisitResourceFriction | undefined = advanced
      ? advanced.defaultAssignmentFriction
      : freezeDefaults ? "frozen" : undefined;

    const { input, optimizerLogInitialState } = buildDriveOptimizerRequestData(
      localVisits,
      localCalendarBlocks,
      visitResources,
      localShifts,
      careRequirementVisits,
      orgConfig,
      shufflerLeadTime,
      arrivalWindowAdjustment,
      providerData?.commandIndexProviders?.providers ?? [],
      defaultStartTimeFriction,
      defaultAssignmentFriction,
    );
    optimizerLogInitialStateRef.current = optimizerLogInitialState;
    runDataOptimizerQuery({ variables: { input } });
  }, [careRequirementVisits, localCalendarBlocks, localShifts, localVisitResources, localVisits, orgConfig, providerData?.commandIndexProviders?.providers, runDataOptimizerQuery, shufflerLeadTime]);

  const renderProviderResource = useCallback((resource: MbscResource) => {
    const providerShifts = localShifts.filter(shift => shift.provider?.id === resource.id && shift.status === ShiftStatusEnum.Confirmed);
    const hasCareTeam = providerShifts.some(shift => 
      continuityOfCareShiftIds.has(shift.id)
    );
    return (
      <ProviderCalendarItem
        resource={resource}
        loading={anyLoading}
        handleViewRoutes={handleRouteModalOpen}
        isCareTeam={hasCareTeam}
        providerShifts={providerShifts}
        onGroupToggle={toggleCollapsedGroup}
        optimizeDisabled={isOptimizeDisabled}
        onOptimizeProvider={(providerId, ignoreShouldKeepOptimizationResult) => runDriveTimeOptimization(
          undefined,
          providerId,
          undefined,
          undefined,
          ignoreShouldKeepOptimizationResult,
        )}
        handleOnAddVisit={(resourceId) => {
          const provider = providerData?.commandIndexProviders?.providers.find((p) => p.id === resourceId);
          if (provider) {
            setOverrideModalState({
              mode: 'provider-start',
              provider: {
                id: provider.id,
                firstName: provider.firstName || '',
                lastName: provider.lastName || '',
                providerType: provider.providerType ? {
                  id: provider.providerType.id,
                  name: provider.providerType.name || '',
                } : null,
              }
            });
          }
        }}
      />
    );
  }, [anyLoading, continuityOfCareShiftIds, handleRouteModalOpen, isOptimizeDisabled, localShifts, runDriveTimeOptimization, toggleCollapsedGroup]);

  const renderProviderResourceHeader = useCallback(() => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "100%",
        background: "white",
        padding: "6px 8px",
        gap: 0.5,
        overflow: "hidden",
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <TextField
        size="small"
        placeholder="Search"
        value={providerSearchQuery}
        onChange={(e) => {
          setProviderSearchQuery(e.target.value);
        }}
        sx={{
          flexGrow: 1,
          minWidth: "100px",
          maxWidth: "calc(100% - 60px)",
          "& .MuiOutlinedInput-root": {
            height: "28px",
            backgroundColor: "white",
            fontSize: "0.875rem",
            "&:hover": {
              backgroundColor: "white",
            },
            "&.Mui-focused": {
              backgroundColor: "white",
            },
          },
          "& .MuiInputBase-input": {
            padding: "4px 8px",
          },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: "4px" }}>{
                providerSearchQuery ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setProviderSearchQuery("")}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }
        }}
      />
      <FilterMenu filters={filters} onChange={setFilters} viewMode={viewMode} />
    </Box>
  ), [filters, providerSearchQuery, setProviderSearchQuery, viewMode]);

  const renderCustomDay = useCallback(() => <VisitStatusKey />, []);

  const renderProviderResourceEmpty = useCallback(() => {
    return <Alert severity="info" sx={{ pl: 1, pr: 1, width: "100%", boxSizing: "border-box" }}>
      <AlertTitle>No shifts</AlertTitle>
      No shifts or visits to display.
    </Alert>
  }, []);

  const renderPatientResource = useCallback((resource: MbscResource) => (
    <PatientCalendarItem 
      resource={resource} 
      onAddVisit={(patientId, firstName, lastName) => {
        setOverrideModalState({
          mode: 'patient-start',
          patient: { id: patientId, firstName, lastName }
        });
      }}
    />
  ), []);

  const renderPatientResourceHeader = useCallback(() => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        height: "100%",
        background: "white",
        padding: "6px 8px",
        gap: 0.5,
        overflow: "hidden",
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <TextField
        size="small"
        placeholder="Search"
        value={patientSearchQuery}
        onChange={(e) => {
          setPatientSearchQuery(e.target.value);
        }}
        sx={{
          pr: 0,
          flexGrow: 1,
          minWidth: "100px",
          maxWidth: "calc(100% - 60px)",
          "& .MuiOutlinedInput-root": {
            height: "28px",
            backgroundColor: "white",
            fontSize: "0.875rem",
            "&:hover": {
              backgroundColor: "white",
            },
            "&.Mui-focused": {
              backgroundColor: "white",
            },
          },
          "& .MuiInputBase-input": {
            padding: "4px 8px",
          },
        }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end" sx={{ marginLeft: "4px" }}>
                {patientSearchQuery ? (
                  <IconButton
                    size="small"
                    aria-label="Clear search"
                    onClick={() => setPatientSearchQuery("")}
                    sx={{ p: 0.25 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                ) : null}
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }
        }}
      />
      <FilterMenu filters={filters} onChange={setFilters} viewMode={viewMode} />
    </Box>
  ), [filters, patientSearchQuery, setPatientSearchQuery, viewMode]);

  const renderPatientResourceEmpty = useCallback(() => (
    <Alert severity="info" sx={{ pl: 1, pr: 1}}>
      <AlertTitle>No patients</AlertTitle>
      No visits to display.
    </Alert>
  ), []);

  const renderResourceHeader = isPatientView
    ? renderPatientResourceHeader
    : renderProviderResourceHeader;

  const renderResource = isPatientView
    ? renderPatientResource
    : renderProviderResource;
  const renderResourceEmpty = isPatientView ? renderPatientResourceEmpty : renderProviderResourceEmpty;

  const renderEvent = useCallback((event: CustomMbscCalendarEventData) => (
    renderCalendarEvent(event, {
      date,
      viewMode,
      providerLookup,
      utilizationPerShift,
      lookups: calendarEventRenderLookups,
      orgConfig,
    })
  ), [calendarEventRenderLookups, date, orgConfig, providerLookup, utilizationPerShift, viewMode]);

  const uploadOptimizerLogInitialState = async (
    optimizerLogId: number,
    optimizerLogInitialState: OptimizerInitialState
  ) => {
    if (window.location.hostname === "localhost") {
      console.log("optimizer log initial state", optimizerLogId, optimizerLogInitialState);
      return;
    }

    const { data } = await createOptimizerLogInitialStateUploadUrl({ variables: { optimizerLogId } });

    const uploadUrl = data?.createOptimizerLogInitialStateUploadUrl;
    if (!uploadUrl) {
      console.error("Missing optimizer log initial state upload URL.", optimizerLogId);
      return;
    }

    try {
      await retryFetch(() => fetch(uploadUrl, {
        method: "PUT",
        body: JSON.stringify(optimizerLogInitialState),
        headers: { "Content-Type": "application/json" },
      }));
    } catch (error) {
      // Print the initial state to the console for debugging locally
      console.log("initial state", optimizerLogInitialState);
    }
  };

  const handleDriveTimeOptimizerData = useCallback((
    data: DriveTimeOptimizerQuery | undefined,
  ) => {
    const result = data?.driveTimeOptimizer;
    if (!result) return false;
    setOptimizerLogId(result.optimizerLogId);

    const optimizerLogInitialState = optimizerLogInitialStateRef.current;
    optimizerLogInitialStateRef.current = null;
    if (result.optimizerLogId && optimizerLogInitialState) {
      uploadOptimizerLogInitialState(result.optimizerLogId, optimizerLogInitialState);
    }

    setDriveTimeOptimizerIcon(result.success ? 'check' : 'error');
    driveTimeOptimizerIconTimeoutRef.current = setTimeout(() => {
      setDriveTimeOptimizerIcon('default');
    }, 3000);

    dispatch({
      type: 'SET_OPTIMIZER_ASSIGNMENT_IDS',
      payload: result.assignments.map((assignment) => assignment.visitResourceId),
    });

    const updates: Record<string, LocalVisitResourcePatch> = {};

    for (const assignment of result.assignments) {
      const existingResource = localVisitResources.find(vr => vr.id === assignment.visitResourceId);
      if (!existingResource) continue; // skip if not found locally

      const patch: LocalVisitResourcePatch = {};

      if (assignment.providerId !== existingResource.providerId) {
        patch.providerId = assignment.providerId;
      }

      if (assignment.startTime !== existingResource.startTime) {
        patch.startTime = assignment.startTime;
      }

      if (assignment.endTime !== existingResource.endTime) {
        patch.endTime = assignment.endTime;
      }

      // Only include non-empty patches
      if (Object.keys(patch).length > 0) {
        updates[assignment.visitResourceId.toString()] = patch;
      }
    }

    // Unassign any skipped resources from their current providers and remove drive time
    for (const skip of result.skipped) {
      updates[skip.visitResourceId.toString()] = {providerId: null, startOfShiftDriveTime: 0, endOfShiftDriveTime: 0, interVisitDriveTime: 0};
    }

    const requiresDriveTimeUpdate = Object.keys(updates).length > 0;

    if (Object.keys(updates).length > 0) {
      dispatch({
        type: 'SET_ORIGINAL_VISIT_RESOURCES',
        payload: Array.from(new Set([
          ...result.assignments.map((assignment) => assignment.visitResourceId),
          ...result.skipped.map((skip) => skip.visitResourceId),
        ])),
      });
      dispatch({ type: 'MODIFY_RESOURCES', payload: updates });
    }

    dispatch({ type: 'SET_SKIPPED_VISIT_RESOURCES', payload: {skipped: result.skipped} });
    return requiresDriveTimeUpdate;
  }, [dispatch, localVisitResources, uploadOptimizerLogInitialState]);

  const maybeCloseVisitPopover = useCallback(() => {
    if (!visitIdToBeCanceled) {
      setPopoverEvent(null);
    }
  }, [visitIdToBeCanceled]);

  const onEventClick = useCallback((e: MbscEventClickEvent) => {
    // set selected event
    // open popper with element
    popoverEventElement.current = e.domEvent.currentTarget;
    if (e.event.type === "shift") {
      setPopoverEvent({ type: "shift", id: e.event.referenceId });
    }
    else if (e.event.type === "visit_resource") {
      const visitResourceEvent = e.event as unknown as CustomMbscCalendarEventData
      const localVisitResource = localVisitResources.find(vr => vr.id === visitResourceEvent.referenceId)!;
      setPopoverEvent({ type: "visit", id: localVisitResource.visit.id });
    } else if (e.event.type === "calendar_block" || e.event.type === "calendar_block_filter") {
      const calendarBlockEvent = e.event as unknown as CustomMbscCalendarEventData
      const localCalendarBlock = localCalendarBlocks.find(cb => cb.id === calendarBlockEvent.referenceId)!;
      setPopoverEvent({ type: "calendar_block", id: localCalendarBlock.id });
    }
  }, [localCalendarBlocks, localVisitResources]);

  const handleRouteModalClose = useCallback(() => {
    setRouteModalOpen(false);
    setRouteLocalDataSnapshot(null);
  }, []);

  const handleEditVisit = useCallback((visitId: number) => {
    setOverrideModalState({ mode: 'edit', visitId });
  }, []);

  const saveNewVisits = useCallback(async () => {
    for (const visit of pendingNewVisitsToBeSaved) {
      const metaData = visitMetaDataLookup[visit.id]

      const startTime = new Date(Math.min(...visit.visitResources.map(vr => new Date(vr.startTime).getTime())));

      try {
        const newlyCreated = await scheduleOverrideVisit({
          variables: {
            taskId: metaData.taskId,
            patientId: visit.patient?.id,
            serviceLineId: metaData.serviceLineId,
            visitTypeId: visit.visitTypeId,
            startTime: startTime.toISOString(),
            resources: visit.visitResources.map((vr) => ({
              providerId: vr.providerId,
              resourceRequirementId: vr.visitResourceRequirementId
            })),
            serviceIds: metaData.serviceIds,
            duration: metaData.duration,
          },
        });
        // Since we are doing these one at a time, the drive time the DB returns may not be the final drive time.
        // In this particular case, we just want to ignore those drive times, so replace them with the ones on the local object.
        const resultVisit = newlyCreated.data?.scheduleOverrideVisit;
        if (resultVisit) {
          for (const vr of resultVisit.visitResources) {
            const localVR = visit.visitResources.find(local => local.providerId === vr.providerId);
            if (localVR) {
              vr.startOfShiftDriveTime = localVR.startOfShiftDriveTime;
              vr.interVisitDriveTime = localVR.interVisitDriveTime;
              vr.endOfShiftDriveTime = localVR.endOfShiftDriveTime;
            }
          }
        }

        dispatch({ type: 'VISIT_CREATE_FINISHED', payload: { oldVisitId: visit.id, result: resultVisit } });
      } catch (error) {
        console.error(`Failed to schedule visit for patient:`, error);
        updateErrorForVisit(visit, `Failed to schedule visit for patient ${visit.patient?.firstName} ${visit.patient?.lastName}`, 'save')
      }
    }
  }, [dispatch, pendingNewVisitsToBeSaved, scheduleOverrideVisit, updateErrorForVisit, visitMetaDataLookup]);

  const updateExistentVisits = useCallback(async () => {
    const updatedIds: number[] = []
    const vrsByProvider = Object.values(groupBy(existentVisitResourcesToBeUpdated, "providerId"))
    setUpdateProgress([0, existentVisitResourcesToBeUpdated.length])

    await Promise.all(vrsByProvider.map(async vrsForProvider => {
      for (const visitResource of vrsForProvider) {
        if (!visitResource.id) continue;
        try {
          const {
            id,
            startTime,
            endTime,
            startOfShiftDriveTime,
            endOfShiftDriveTime,
            interVisitDriveTime,
            providerId,
          } = visitResource;
          await updateVisitResourceMutation({
            variables: {
              id,
              startTime,
              endTime,
              startOfShiftDriveTime,
              endOfShiftDriveTime,
              interVisitDriveTime,
              providerId
            }
          });
          updatedIds.push(id)
          setUpdateProgress([updatedIds.length, existentVisitResourcesToBeUpdated.length])
        } catch (error) {
          console.error(`Failed to update visit resource:`, error);
          // @TODO: error handling for updating visit resource
        }
      }
    }))

    setUpdateProgress(null)
    dispatch({ type: 'VISIT_RESOURCE_UPDATE_FINISHED', payload: updatedIds });
  }, [dispatch, existentVisitResourcesToBeUpdated, updateVisitResourceMutation]);

  const updateExistentCalendarBlocks = useCallback(async () => {
    for (const id in modifiedCalendarBlocks) {
      const calendarBlock = modifiedCalendarBlocks[id];
      try {
        await updateCalendarBlockMutation({
          variables: {
            ...calendarBlock,
            id: parseInt(id),
          } as any,
          awaitRefetchQueries: true,
          refetchQueries: ["AvailabilityByTimespan"],
        });
        dispatch({ type: 'CALENDAR_BLOCK_UPDATE_FINISHED', payload: parseInt(id) });
      } catch (error) {
        console.error(`Failed to update calendar block:`, error);
        // @TODO: error handling for updating calendar block
      }
    }
  }, [dispatch, modifiedCalendarBlocks, updateCalendarBlockMutation]);

  const saveAllModifiedVisits = useCallback(async () => {
    await Promise.all([
      saveNewVisits(),
      updateExistentVisits(),
      updateExistentCalendarBlocks(),
    ]);

    await apolloClient.refetchQueries({
      include: ['CommandIndexCombinedData']
    });
    onResetDragDrop();
  }, [apolloClient, onResetDragDrop, saveNewVisits, updateExistentCalendarBlocks, updateExistentVisits]);

  // USE EFFECTS
  useEffect(() => {
    if (dbChangeDetected) {
      snackbar.setErrorMessage("Changes have been made by another user. Your view has been updated.", "info");
      dispatch({ type: 'RESET_DB_CHANGE_DETECTOR' })
    }
  }, [dbChangeDetected, dispatch, snackbar]);

  useEffect(() => {
    if (!pendingOptimization || pendingOptimization.phase !== 'review') {
      return;
    }

    if (shouldWaitForOptimizationDriveTimes({
      requiresDriveTimeUpdate: pendingOptimization.requiresDriveTimeUpdate,
      driveTimesLoading,
      pendingDriveTimeShiftCount: pendingDriveTimeShiftIds.size,
      hasPendingDriveTimeUpdates,
    })) {
      return;
    }

    if (shouldResetOptimizationResult(optimizationIssueStats, appliedIgnoreKeepResults)) {
      onReset();
      setPendingOptimization((current) => current ? { ...current, phase: 'no-optimizations' } : null);
    } else {
      setPendingOptimization(null);
    }
  }, [
    appliedIgnoreKeepResults,
    driveTimesLoading,
    hasPendingDriveTimeUpdates,
    onReset,
    optimizationIssueStats.newTotalDriveTime,
    optimizationIssueStats.newVisitsWithIssuesCount,
    optimizationIssueStats.originalTotalDriveTime,
    optimizationIssueStats.originalVisitsWithIssuesCount,
    pendingDriveTimeShiftIds.size,
    pendingOptimization,
  ]);

  useEffect(() => {
    if (locationState?.visitResourceId) {
      const event = stableCalendarData.find(e => e.type === "visit_resource" && e.referenceId === locationState?.visitResourceId);
      if (event) {
        calendarRef.current?.navigateToEvent(event as any);
      }
    }
  }, [locationState, stableCalendarData]);

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      <Modal
        open={routeModalOpen}
        onClose={handleRouteModalClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
        sx={{
          top: "50%",
        }}
      >
        <Box sx={{ ...style, position: "relative" }}>
          {/* Close button */}
          <IconButton
            aria-label="close"
            onClick={handleRouteModalClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "grey.500",
            }}
          >
            <CloseIcon />
          </IconButton>
          {/* Modal content */}
          {date ? (
            <RouteIndexView
              providerId={routeProviderId}
              providerName={routeProviderName}
              date={date}
              localVisitResources={routeLocalDataSnapshot?.localVisitResources ?? []}
              localCalendarBlocks={routeLocalDataSnapshot?.localCalendarBlocks ?? []}
              localProviderContext={routeLocalDataSnapshot?.providerContext ?? { addressCoordinates: null }}
              localShiftContext={routeLocalDataSnapshot?.shiftContext}
              showUnsavedChangesNote={hasUnsavedRouteChanges(routeProviderId)}
            />
          ) : null}
        </Box>
      </Modal>
      <Box sx={{ width: "100%", display: "flex", alignItems: "center", height: "3.5em" }}>
        {/* Left side - Date controls */}
        <Stack direction="row" alignItems="center" justifyContent="start" spacing={2} sx={{ flexGrow: 1 }}>
          <Button 
            onClick={today} 
            variant="outlined"
          >
            Today
          </Button>
          <Box whiteSpace="nowrap">
            <IconButton size="small" onClick={previousDay} color="primary">
              <ArrowBackIosIcon fontSize="inherit" />
            </IconButton>
            <IconButton size="small" onClick={nextDay} color="primary">
              <ArrowForwardIosIcon fontSize="inherit" />
            </IconButton>
          </Box>
          <Typography
            component={"div"}
            display="inline-block"
            variant="subtitle1"
            whiteSpace="nowrap"
            sx={{
              fontSize: "1.2rem",
              fontWeight: "bold",
            }}
          >
            {dateLabelWithMaybeYear(date)}
          </Typography>
          <DatePickerPopup
            value={date}
            onChange={(newValue) => setDate(newValue!)}
            slotProps={{
              openPickerButton: {
                color: "primary",
                sx: { ml: -3 }
              },
              openPickerIcon: {
                fontSize: "small",
              },
            }}
          />

          <ToggleButtonGroup
            exclusive
            size="small"
            color="primary"
            value={viewMode}
            onChange={handleViewModeChange}
          >
            <ToggleButton value="provider" sx={{ minWidth: 100 }}>Provider</ToggleButton>
            <ToggleButton value="patient" sx={{ minWidth: 100 }}>Patient</ToggleButton>
          </ToggleButtonGroup>

          {!isPatientView && (
            <Button
              onClick={() => navigate("/location")}
              disabled={!hasProviderLocationPermission}
              variant="outlined"
              sx={{
                size: "small",
                color: "info",
              }}
              endIcon={<MapIcon />}
            >
              Providers
            </Button>
          )}

          <Button
            onClick={() => navigate("/visit-location/" + date.toISOString())}
            disabled={false}
            variant="outlined"
            sx={{
              size: "small",
              color: "info",
            }}
            endIcon={<MapIcon />}
          >
            Visits
          </Button>

          {!isPatientView && (
            <>
              <PermissionOrHide permission={Permission.optimizeSchedule}>
                <OptimizeButton
                  disabled={isOptimizeDisabled}
                  status={driveTimeOptimizerIcon}
                  onAssignMissingProviders={(ignoreShouldKeepOptimizationResult) => runDriveTimeOptimization(
                    undefined,
                    undefined,
                    true,
                    undefined,
                    ignoreShouldKeepOptimizationResult,
                  )}
                  onOptimizeAdvanced={(settings) => runDriveTimeOptimization(
                    settings.arrivalWindowAdjustment,
                    undefined,
                    false,
                    settings,
                    settings.ignoreShouldKeepOptimizationResult,
                  )}
                />
              </PermissionOrHide>
              <AddToCalendarMenu date={date} providers={activeProvidersList || []} />
            </>
          )}
        </Stack>

        <Stack direction="row" alignItems="center" justifyContent="end" spacing={2}>
          <CommandCenterStats
            localVisits={localVisits}
            localShifts={localShifts}
            localCalendarBlocks={localCalendarBlocks}
            loading={anyLoading}
          />
        </Stack>
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: 'calc(100vh - 130px)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {isOptimizationTimelineBlocked && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1200,
              backgroundColor: 'background.paper',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 2,
              px: 3,
            }}
          >
            {pendingOptimization?.phase === 'no-optimizations' ? (
              <>
                <InfoIcon color="info" sx={{ fontSize: 48 }} />
                <Typography variant="subtitle1">No optimizations found</Typography>
                <Button variant="contained" onClick={() => setPendingOptimization(null)}>OK</Button>
              </>
            ) : (
              <>
                <CircularProgress />
                <Typography variant="subtitle1">
                  {pendingOptimization?.phase === 'query' ? 'Optimizing schedule...' : 'Preparing optimization review...'}
                </Typography>
              </>
            )}
          </Box>
        )}

        <PermissionOrHide permission={Permission.createDragEvents}>
          <Box sx={{ flexShrink: 0 }}>
            <CalendarHeaderContent
              date={date}
              providers={providerData?.commandIndexProviders?.providers ?? []}
              localVisits={localVisits}
              localCalendarBlocks={localCalendarBlocks}
              careRequirementVisits={careRequirementVisits}
              // The visitMetaDataLookup serves as an indirect measure of whether a visitRequest has a corresponding visit
              // We can't do it directly, because visits don't have a taskId in the DB, and the temporary id is subject to change
              // Managing in this roundabout way allows us to not wait to re-query tasks on save.
              visitRequests={localVisitRequests.filter((request) => !Object.values(visitMetaDataLookup).some((meta) => meta.taskId === request.taskId))}
              visitEvents={providerVisitEvents}
              isDirty={modifiedResourceIds.size > 0 || Object.keys(modifiedCalendarBlocks).length > 0}
              driveTimeLoading={isOptimizationReviewLoading}
              isSavingChanges={isSavingVisitRequest || isSavingVisitResource || isSavingCalendarBlock}
              errorsAndWarnings={errorsAndWarnings}
              isPatientView={isPatientView}
              hasMoreActivePatients={hasMorePatients && filters.showAllPatients}
              activePatientsDisplayLimit={ACTIVE_PATIENTS_LIMIT}
              onSave={async () => {
                saveAllModifiedVisits();
              }}
              onReset={onReset}
              showGhosts={showOriginalPositions}
              onToggleGhosts={setShowOriginalPositions}
              driveTimeOptimizations={driveTimeOptimizations}
              optimizationIssueStats={optimizationIssueStats}
              skippedVisitResources={skippedVisitResources}
              optimizerLogId={optimizerLogId}
            />
          </Box>
        </PermissionOrHide>

        {/* Calendar container - fills remaining space */}
        <Box
          sx={{
            mt: 1.25,
            flexGrow: 1,
            minHeight: 0, // Critical for flex containers to properly constrain height
            display: 'flex',
            flexDirection: 'column',
            position: "relative",
            overflow: 'hidden', // No scrollbar here
          }}
        >
          {(isSavingVisitRequest || isSavingVisitResource || isSavingCalendarBlock) && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
                zIndex: 1000,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                Saving changes{updateProgress ? ` (${updateProgress[0]}/${updateProgress[1]})` : ""}...
                <Box sx={{ ml: 2, display: 'inline-block' }}>
                  <CircularProgress size={24} />
                </Box>
              </Typography>
            </Box>
          )}

          <StyledCalendar
            ref={calendarRef}
            // Note tests/patientViewMode.spec.ts depends on this class
            className={isPatientView ? 'patient-calendar' : undefined}
            renderScheduleEvent={renderEvent}
            dragTimeStep={5}
            renderBufferBefore={beforeBuffer}
            showTooltip={false}
            renderBufferAfter={afterBuffer}
            renderHeader={() => null}
            renderResourceHeader={renderResourceHeader}
            renderResource={renderResource}
            renderResourceEmpty={renderResourceEmpty}
            renderDay={renderCustomDay}
            dataTimezone={"UTC"}
            timezonePlugin={dayjsTimezone}
            displayTimezone={Intl.DateTimeFormat().resolvedOptions().timeZone}
            showEventBuffer
            dateFormatLong="DDD MMMM D, YYYY"
            timeFormat={getTimeFormat({ timeDisplay: timeFormatPreference, ampm: true, showTimezone: false })}
            view={CALENDAR_VIEW_CONFIG}
            dragToMove={!isPatientView}
            externalDrop={!isPatientView}
            resources={stableResources}
             // @ts-ignore: stableCalendarData date string
            data={stableCalendarData}
            colors={colors}
            selectedDate={calendarDateTime}
            connections={connections}
            themeVariant="light"
            height="100%"
            onEventClick={onEventClick}
            onEventCreate={isPatientView ? undefined : onEventCreate}
            onEventUpdate={isPatientView ? undefined : onEventUpdate}
            onEventDragEnd={dragHandlersEnabled ? onEventDragEnd : undefined}
            onEventDragStart={dragHandlersEnabled ? onEventDragStart : undefined}
            showEventTooltip={false}
          />
        </Box>
      </Box>

      {overrideModalState && (
        <CreateOverrideVisit
          key={calendarDateTime?.getTime() + "-" + (overrideModalState.mode === 'provider-start' ? overrideModalState.provider.id : '') + `-${overrideModalState.mode === 'edit' ? overrideModalState.visitId : 'new'}`}
          selectedProvider={selectedProviderForOverrideVisit}
          handleClose={() => {
            setOverrideModalState(null);
          }}
          calendarDateTime={calendarDateTime ? (isSameDay(calendarDateTime, new Date()) ? roundTime15(new Date()) : calendarDateTime) : new Date()}
          onSuccess={(visitId) => {
            setLastAddedVisitId(visitId);
            setTimeout(() => {
              setLastAddedVisitId(null);
            }, 2000);
          }}
          visitId={overrideModalState.mode === 'edit' ? overrideModalState.visitId : null}
          prefilledPatient={overrideModalState.mode === 'patient-start' ? overrideModalState.patient : undefined}
        />
      )}

      {popoverEvent && (
        <CalendarPopup
          isOpen={true}
          visitAnchor={popoverEventElement.current}
          onClose={() => maybeCloseVisitPopover()}
        >
          {popoverEvent.type === "visit" && (() => {
            const visit = localVisits.find((lv) => lv.id === popoverEvent.id);
            if (!visit) return;
            const metaData = visitMetaDataLookup[visit.id]
            return metaData && visit ? (
              <VisitRequestPopup
                visit={visit}
                visitMetaData={metaData}
                onClose={() => maybeCloseVisitPopover()}
                onRemoveVisit={(visit) => {
                  dispatch({ type: 'REMOVE_VISIT', payload: visit.id });
                  maybeCloseVisitPopover();
                }}
              />
            ) : (
              <VisitPopup
                key={popoverEvent.id}
                visit={visit}
                onClose={() => maybeCloseVisitPopover()}
                onCancel={(visitId) => {
                  setVisitIdToBeCanceled(visitId);
                  maybeCloseVisitPopover();
                }}
                onEdit={(visitId) => {
                  handleEditVisit(visitId);
                  maybeCloseVisitPopover();
                }}
              />
            )
          })()}
          {popoverEvent.type === "shift" && (() => {
            const shift = localShifts.find((ls) => ls.id === popoverEvent.id);
            return shift ? (
              <ShiftPopup
                key={shift.id}
                shift={shift}
                shiftCenters={Object.values(orgConfig["shiftCenters"]).filter(sc => sc !== undefined)}
                onClose={() => maybeCloseVisitPopover()}
              />
            ) : null;
          })()}
          {popoverEvent.type === "calendar_block" && (() => {
            const calendarBlock = localCalendarBlocks.find((cb) => cb.id === popoverEvent.id);
            return calendarBlock ? (
              <CalendarBlockPopup
                key={calendarBlock.id}
                calendarBlock={calendarBlock}
                onClose={() => maybeCloseVisitPopover()}
              />
            ) : null;
          })()}
        </CalendarPopup>
      )}

      {medarriveEvent?.event.type === "visit_request" ? (
        <MultiResourceProviderAssign
          key={`${medarriveEvent.event.id}-${getAssignedProviders(medarriveEvent).map((p) => p.providerId).join('-')}`}
          startTime={medarriveEvent.eventStart.toISOString()}
          endTime={medarriveEvent.eventEnd.toISOString()}
          visit={getLocalVisitRequest(medarriveEvent)}
          orgConfig={orgConfig}
          open={shouldOpenMultiResourceAssignPopup}
          defaultAssignedResourceProviderMap={medarriveEvent.assignedProvidersToResourcesMap}
          providers={providerData?.commandIndexProviders?.providers ?? []}
          shifts={dragDropState.localShifts}
          onClose={() => setShouldOpenMultiResourceAssignPopup(false)}
          onSave={onMultiResourceAssign}
          onCancel={() => {
            setMedarriveEvent(null);
            dragDropState.dispatch({type: 'REMOVE_VISIT', payload: medarriveEvent.event.referenceId});
            setShouldOpenMultiResourceAssignPopup(false);

            // Inform mobiscroll that it needs to remove the created event, because we're basically cancelling
            // the drop after the fact.
            // @ts-ignore Date types are incompatible, not relevant here
            calendarRef.current?.removeEvent(medarriveEvent.event)
          }}
        />
      ) : null}

      {visitIdToBeCanceled && <CancelVisitDialog
        visitId={visitIdToBeCanceled}
        onClose={() => setVisitIdToBeCanceled(null)}
        onSuccess={() => setVisitIdToBeCanceled(null)}
      />}
    </Box>
  );
}

const UPDATE_VISIT_RESOURCE = gql`
  mutation UpdateVisitResourceMutation(
    $id: Int!,
    $endOfShiftDriveTime: Int,
    $startOfShiftDriveTime: Int,
    $interVisitDriveTime: Int,
    $startTime: String,
    $endTime: String,
    $providerId: Int
  )
  @permission(requires: createDragEvents)
  {
    updateVisitResourceObject(
      id: $id,
      endOfShiftDriveTime: $endOfShiftDriveTime,
      startOfShiftDriveTime: $startOfShiftDriveTime,
      interVisitDriveTime: $interVisitDriveTime,
      startTime: $startTime,
      endTime: $endTime,
      providerId: $providerId
    ) {
      id
    }
  }
`;

gql`
  mutation UpdateCalendarBlockMutation(
    $id: Int!,
    $providerId: Int,
    $startTime: String,
    $endTime: String,
    $startOfShiftDriveTime: Int,
    $endOfShiftDriveTime: Int,
    $interVisitDriveTime: Int
  )
  @permission(requires: createDragEvents)
  {
    updateCalendarBlock(
      id: $id,
      providerId: $providerId,
      startTime: $startTime,
      endTime: $endTime,
      startOfShiftDriveTime: $startOfShiftDriveTime,
      endOfShiftDriveTime: $endOfShiftDriveTime,
      interVisitDriveTime: $interVisitDriveTime
    ) {
      id
      providerId
      startTime
      endTime
      startOfShiftDriveTime
      endOfShiftDriveTime
      interVisitDriveTime
    }
  }
`;

const COMMAND_ACTIVE_PATIENTS_QUERY = gql(`
  query CommandActivePatients(
    $orderBy: ActivePatientOrderBy!
    $offset: Int!
    $limit: Int!
  )
  @permission(requires: useWebApp)
  {
    activePatients(
      offset: $offset
      limit: $limit
      orderBy: $orderBy
    ) {
      id
      firstName
      lastName
    }
  }
`);

export const SCHEDULE_VISIT_FROM_VISIT_REQUEST = gql`
  mutation ScheduleVisitFromVisitRequest(
    $patientId: Int!,
    $serviceLineId: Int!,
    $visitTypeId: Int!,
    $startTime: String!,
    $resources: [NewOverrideVisitResourceInput!]!,
    $serviceIds: [Int!]!,
    $duration: Int!,
    $visitWarningLogId: Int,
    $taskId: Int
  )
  @permission(requires: createDragEvents, check: [userInServiceLine])
  {
    scheduleOverrideVisit(
      patientId: $patientId,
      serviceLineId: $serviceLineId,
      visitTypeId: $visitTypeId,
      startTime: $startTime,
      resources: $resources,
      serviceIds: $serviceIds,
      duration: $duration,
      visitWarningLogId: $visitWarningLogId,
      taskId: $taskId
    ) {
      id
      visitResources {
        id
        providerId
        startOfShiftDriveTime
        interVisitDriveTime
        endOfShiftDriveTime
      }
    }
  }
`;

export const DRIVE_TIME_OPTIMIZER_QUERY = gql`
  query driveTimeOptimizer($input: DriveTimeOptimizerInput!) 
  @permission(requires: optimizeSchedule)
  {
    driveTimeOptimizer(input: $input) {
      optimizerLogId
      assignments {
        visitResourceId
        providerId
        startTime
        endTime
      }
      skipped {
        visitResourceId
        reasons
      }
      optimizerLogData
      success
    }
  }
`;

const CREATE_OPTIMIZER_LOG_INITIAL_STATE_UPLOAD_URL = gql`
  mutation CreateOptimizerLogInitialStateUploadUrl(
    $optimizerLogId: Int!
  )
  @permission(requires: optimizeSchedule)
  {
    createOptimizerLogInitialStateUploadUrl(
      optimizerLogId: $optimizerLogId
    )
  }
`;
